import { supabase } from "./supabaseClient";
import { captureAttribution, readAttribution, trackMarketingEvent } from "./marketingAttribution";

const compact = (value, max = 120) => {
  if (typeof value !== "string") return null;
  const normalized = value.trim().replace(/\s+/g, " ");
  return normalized ? normalized.slice(0, max) : null;
};

export const isTesterCampaign = (location = typeof window !== "undefined" ? window.location : undefined) => {
  const params = new URLSearchParams(location?.search || "");
  const explicit = String(params.get("tester") || "").toLowerCase();
  if (["1", "true", "si", "sí", "yes"].includes(explicit)) return true;
  return ["utm_campaign", "utm_content", "utm_source"]
    .map(key => String(params.get(key) || "").toLowerCase())
    .some(value => value.includes("tester") || value.includes("comenta-tester") || value.includes("comenta_tester"));
};

export const persistedRpc = async (client, name, args = {}) => {
  const { data, error } = await client.rpc(name, args);
  if (error) throw error;
  if (data === null || data === undefined) throw new Error(`${name} did not confirm persistence`);
  return data;
};

export const syncCommunityProfile = async (client = supabase) => {
  const attribution = captureAttribution();
  return persistedRpc(client, "community_sync_profile", {
    p_source: compact(attribution.source, 100),
    p_medium: compact(attribution.medium, 100),
    p_campaign: compact(attribution.campaign, 120),
    p_content: compact(attribution.content, 120),
  });
};

export const loadCommunitySnapshot = async (client = supabase) =>
  persistedRpc(client, "community_snapshot");

export const requestTesterInterest = async (client = supabase) => {
  const attribution = readAttribution();
  const row = await persistedRpc(client, "request_tester_interest", {
    p_campaign: compact(attribution.campaign, 120),
    p_source: compact(attribution.source, 100),
    p_medium: compact(attribution.medium, 100),
    p_content: compact(attribution.content, 120),
  });
  await trackMarketingEvent({ supabase: client, eventName: "tester_interest", metadata: { request_id: row.id || null } });
  return row;
};

export const submitRestaurantClaim = async ({ restaurantId, claimantRole, evidenceType, evidenceNote, contactPreference }, client = supabase) => {
  const row = await persistedRpc(client, "submit_restaurant_claim", {
    p_restaurant_id: Number(restaurantId),
    p_claimant_role: claimantRole,
    p_evidence_type: evidenceType,
    p_evidence_note: evidenceNote,
    p_contact_preference: contactPreference,
  });
  await trackMarketingEvent({ supabase: client, eventName: "restaurant_claim_submitted", metadata: { claim_id: row.id || null, restaurant_id: Number(restaurantId) } });
  return row;
};

export const loadClaimableRestaurants = async (client = supabase) => {
  const { data, error } = await client
    .from("restaurants")
    .select("id,name,location")
    .eq("pending", false)
    .eq("moderation_status", "approved")
    .order("name", { ascending: true });
  if (error) throw error;
  return Array.isArray(data) ? data : [];
};

export const recordBehaviorEvidence = async ({ eventType, placeId = null, numericValue = null, unit = null, source = "ui_action", clientSessionId = null, evidenceKey = null, metadata = {} }, client = supabase) =>
  persistedRpc(client, "record_behavior_evidence", {
    p_event_type: eventType,
    p_place_id: placeId == null ? null : Number(placeId),
    p_numeric_value: numericValue,
    p_unit: compact(unit, 40),
    p_evidence_source: source,
    p_client_session_id: compact(clientSessionId, 100),
    p_evidence_key: compact(evidenceKey, 140),
    p_metadata: metadata && typeof metadata === "object" ? metadata : {},
  });

export const trackSep10Landing = async (client = supabase) => {
  const storage = typeof window !== "undefined" ? window.sessionStorage : null;
  const key = "venaqqi_sep10_landing_tracked_v1";
  try {
    if (storage?.getItem(key)) return false;
  } catch { /* storage is optional */ }
  await trackMarketingEvent({ supabase: client, eventName: "landing", metadata: { tester_campaign: isTesterCampaign() } });
  try { storage?.setItem(key, "1"); } catch { /* storage is optional */ }
  return true;
};

export const trackCommunityOpened = (client = supabase) =>
  trackMarketingEvent({ supabase: client, eventName: "community_opened", metadata: { tester_campaign: isTesterCampaign() } });
